import { useState, useEffect, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/common";
import { PenSquare, Plus, Trash2, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";

function EditableList({ label, items, onChange, placeholder }) {
  const set = (i, v) => onChange(items.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={it} onChange={(e) => set(i, e.target.value)} placeholder={placeholder}
            data-testid={`content-item-${label}-${i}`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}
            data-testid={`content-remove-${label}-${i}`} title="Ta bort">
            <X className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`content-add-${label}`}>
        <Plus className="mr-1 h-4 w-4" /> Lägg till rad
      </Button>
    </div>
  );
}

function BrandingEditor() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/content/branding").then((r) => setB(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!b) return null;
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/content/config/branding", b); setB(r.data); toast.success("Varumärket sparat – syns i appen."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="space-y-4 p-6" data-testid="branding-editor">
      <div className="text-sm font-semibold">Produktnamn & tagline (visas i sidomeny/header för alla organisationer)</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label>Produktnamn</Label><Input value={b.product_name} onChange={(e) => setB({ ...b, product_name: e.target.value })} data-testid="brand-name" /></div>
        <div><Label>Tagline</Label><Input value={b.tagline} onChange={(e) => setB({ ...b, tagline: e.target.value })} data-testid="brand-tagline" /></div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="brand-save"><Save className="mr-1 h-4 w-4" />{saving ? "Sparar…" : "Spara varumärke"}</Button>
    </Card>
  );
}

const PRICE_MODULES = [
  ["schema", "Schemaläggning"], ["rotation", "Rotation"], ["internrekrytering", "Internrekrytering"],
  ["enkater", "Enkäter"], ["flode", "Flöde & utskick"], ["utbildningar", "Utbildningar"],
  ["arbetsmiljo", "Arbetsmiljö"], ["uppdrag", "Uppdrag"], ["bibliotek", "Policys & rutiner"], ["itsupport", "IT & Admin-kanal"], ["placement", "Kund & placering"],
];

function PricingEditor() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/content/config/pricing").then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!p) return null;
  const setMod = (k, v) => setP({ ...p, module_prices: { ...p.module_prices, [k]: v } });
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/content/config/pricing", p); setP(r.data); toast.success("Prislistan sparad – syns för organisationer."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="space-y-4 p-6" data-testid="pricing-editor">
      <div className="text-sm font-semibold">Prislista (master admin – syns för organisationer i skräddaren & på dashboarden)</div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Grundpaket – namn</Label><Input value={p.base_label} onChange={(e) => setP({ ...p, base_label: e.target.value })} data-testid="price-base-label" /></div>
        <div><Label>Grundpris</Label><Input type="number" value={p.base_price} onChange={(e) => setP({ ...p, base_price: e.target.value })} data-testid="price-base" /></div>
        <div><Label>Valuta / period</Label><div className="flex gap-2"><Input value={p.currency} onChange={(e) => setP({ ...p, currency: e.target.value })} className="w-16" /><Input value={p.period} onChange={(e) => setP({ ...p, period: e.target.value })} placeholder="/mån" /></div></div>
      </div>
      <div className="text-xs font-medium text-muted-foreground">Tilläggspris per modul</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {PRICE_MODULES.map(([k, l]) => (
          <div key={k} className="flex items-center gap-2"><span className="flex-1 text-sm">{l}</span><Input type="number" value={p.module_prices?.[k] ?? 0} onChange={(e) => setMod(k, e.target.value)} className="w-28" data-testid={`price-mod-${k}`} /></div>
        ))}
      </div>
      <div><Label>Notering (visas om inga priser satts)</Label><Input value={p.note || ""} onChange={(e) => setP({ ...p, note: e.target.value })} data-testid="price-note" /></div>
      <Button onClick={save} disabled={saving} data-testid="price-save"><Save className="mr-1 h-4 w-4" />{saving ? "Sparar…" : "Spara prislista"}</Button>
    </Card>
  );
}

export default function ContentEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/content/landing"); setForm(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/content/landing", form);
      setForm(r.data);
      toast.success("Texten är sparad och syns nu på inloggningssidan.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Återställa till standardtexten? Dina ändringar tas bort.")) return;
    try { const r = await api.delete("/content/landing"); setForm(r.data); toast.success("Återställt till standard."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!form) return <div className="p-6 text-sm text-muted-foreground">Laddar…</div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="Text-editerare" icon={PenSquare}
        subtitle="Redigera eller ta bort texten på inloggningssidan (hero). Ändringar syns direkt för alla besökare." />

      <Card className="space-y-5 p-6">
        <EditableList label="Rubrikrader" items={form.heading}
          onChange={(v) => setForm({ ...form, heading: v })} placeholder="T.ex. Rätt kompetens." />

        <div className="space-y-2">
          <Label>Ingress</Label>
          <Textarea rows={4} value={form.ingress} onChange={(e) => setForm({ ...form, ingress: e.target.value })}
            data-testid="content-ingress" placeholder="Beskrivande text under rubriken" />
        </div>

        <EditableList label="Punktlista" items={form.features}
          onChange={(v) => setForm({ ...form, features: v })} placeholder="T.ex. Installerbar som app (PWA)" />

        <div className="space-y-2">
          <Label>Fottext</Label>
          <Input value={form.footer || ""} onChange={(e) => setForm({ ...form, footer: e.target.value })}
            data-testid="content-footer" placeholder="Liten text längst ned" />
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t pt-4">
          <Button onClick={save} disabled={saving} data-testid="content-save-btn">
            <Save className="mr-1 h-4 w-4" /> {saving ? "Sparar…" : "Spara"}
          </Button>
          <Button variant="outline" onClick={reset} data-testid="content-reset-btn">
            <RotateCcw className="mr-1 h-4 w-4" /> Återställ standard
          </Button>
        </div>
      </Card>

      <Card className="space-y-3 bg-primary p-6 text-primary-foreground">
        <div className="text-xs uppercase tracking-wide text-blue-200">Förhandsvisning</div>
        <h2 className="font-head text-3xl font-extrabold leading-tight">
          {form.heading.map((h, i) => <span key={i}>{h}<br /></span>)}
        </h2>
        <p className="max-w-md text-blue-100">{form.ingress}</p>
        <ul className="space-y-1 text-sm text-blue-100">
          {form.features.map((f, i) => <li key={i}>• {f}</li>)}
        </ul>
        {form.footer && <p className="pt-2 text-xs text-blue-300">{form.footer}</p>}
      </Card>

      <BrandingEditor />
      <PricingEditor />
    </div>
  );
}
